// progressTracker.js — Sistem tracking hasil latihan & tryout
//
// Catatan migrasi: sudah terhubung ke backend (tabel sesi_latihan) mulai versi ini.
// localStorage tetap dipakai juga (untuk tampilan cepat di HP tanpa nunggu server),
// tapi sumber data untuk Admin Panel sekarang dari database, bukan localStorage.

const STORAGE_LATIHAN  = 'riwayatLatihan';
const STORAGE_TRYOUT   = 'riwayatTryout';
const API_URL = 'https://skortes.com';

// Kirim hasil sesi ke backend (best-effort — kalau gagal/offline, gak bikin app crash,
// localStorage tetap jadi cadangan supaya user tetap lihat riwayatnya sendiri)
//
// dariTryout: true kalau modul ini dikerjakan SEBAGAI BAGIAN dari tryout resmi (bukan
// latihan bebas). Ditandai di server (kolom dari_tryout) supaya laporan "Latihan Harian"
// bisa mengecualikan sesi tryout dan tidak tercampur.
function kirimSesiKeServer(modulId, skor, detail, dariTryout, tryoutId) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return; // belum login / gak ada token, skip kirim ke server
    fetch(`${API_URL}/api/sesi-latihan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ modul_id: modulId, skor: Math.round(skor), detail, dari_tryout: !!dariTryout }),
    }).catch(() => {}); // diamkan kalau gagal, jangan ganggu UX

    // PENTING: sebelumnya progres modul-dalam-tryout HANYA dikirim ke server saat
    // user menekan tombol "Kembali ke Menu" di layar hasil (lihat handleBack di
    // App.js). Kalau user keluar dengan cara lain di layar hasil itu (back
    // browser/gesture HP, tutup tab, dsb — sebelum sempat menekan tombol itu),
    // modul itu tidak pernah tercatat sebagai bagian dari tryout, sehingga hilang
    // dari laporan PDF tryout walau skornya sudah tersimpan di Latihan Harian.
    // Sekarang langsung dikirim di sini juga, begitu hasil selesai dihitung —
    // tidak bergantung lagi pada aksi lanjutan apa pun dari user. Endpoint ini
    // upsert (aman dipanggil dobel dengan handleBack, hasilnya tetap sama).
    if (dariTryout && tryoutId) {
      fetch(`${API_URL}/api/tryouts/${tryoutId}/progres-modul`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ modul_id: modulId, skor: Math.round(skor), detail }),
      }).catch(() => {});
    }
  } catch {}
}

// ─── Bobot modul untuk skor akumulasi tryout ─────────────────
export const BOBOT_MODUL = {
  kraepelin:   3,
  kepribadian: 2,
  logika:      1.5,
  verbal:      1.5,
  figural:     1.5,
  akademik:    1.5,
  armyalpha:   1,
  kecermatan:  1,
  mekanik:     1,
  substitusi:  1,
  kestabilan:  2,
  interpretasigambar: 1,
  interpretasibentuk: 1,
};

export const MODUL_LABEL = {
  kraepelin:   { nama: 'Kraepelin/Pauli',      icon: '🧮', warna: '#C0A062' },
  kecermatan:  { nama: 'Kecermatan',            icon: '✅', warna: '#4ADE80' },
  logika:      { nama: 'Logika & Numerik',      icon: '🔢', warna: '#60A5FA' },
  verbal:      { nama: 'Verbal',                icon: '📖', warna: '#A78BFA' },
  figural:     { nama: 'Figural & Spasial',     icon: '🧩', warna: '#FB923C' },
  kepribadian: { nama: 'Kepribadian',           icon: '🧠', warna: '#F472B6' },
  akademik:    { nama: 'Kemampuan Akademik',    icon: '📚', warna: '#818CF8' },
  armyalpha:   { nama: 'Instruksi Cepat',       icon: '⚡', warna: '#F87171' },
  mekanik:     { nama: 'Tes Bakat Mekanik',     icon: '⚙️', warna: '#FDBA74' },
  substitusi:  { nama: 'Tes Substitusi Simbol', icon: '🔣', warna: '#818CF8' },
  kestabilan:  { nama: 'Kestabilan Pribadi',    icon: '🛡️', warna: '#38BDF8' },
  interpretasigambar: { nama: 'Interpretasi Gambar', icon: '🖼️', warna: '#6366F1' },
  interpretasibentuk: { nama: 'Interpretasi Bentuk', icon: '🔮', warna: '#A855F7' },
};

// ─── LATIHAN HARIAN ───────────────────────────────────────────

/**
 * Catat hasil latihan harian (dipanggil dari tiap modul)
 */
export function catatHasilTes(modulId, skor, detail = {}, dariTryout = false, tryoutId = null) {
  // Jangan catat kalau skor tidak valid (belum benar-benar selesai / gagal dihitung).
  // CATATAN: sebelumnya guard ini juga membuang skor PERSIS 0 (`skor <= 0`), padahal
  // skor 0% adalah hasil YANG SAH kalau user memang sudah mengerjakan sesi tapi jawabannya
  // salah semua (bukan berarti sesi tidak selesai). Ini pernah menyebabkan banyak sesi
  // Kraepelin/Pauli hilang dari statistik & laporan perorangan — waktu itu bug lain
  // (stale closure timer, lalu bug index terbalik) membuat banyak sesi Kraepelin
  // ter-skor 0% secara keliru, dan skor 0 itu langsung dibuang di sini sebelum sempat
  // terkirim ke server, jadi sesi-sesi itu tidak pernah tersimpan sama sekali.
  if (skor === undefined || skor === null || isNaN(skor) || skor < 0) return;
  try {
    const riwayat = ambilRiwayatLatihan();
    riwayat.push({
      id: `${modulId}-${Date.now()}`,
      modulId,
      skor: Math.round(skor),
      tanggal: new Date().toISOString(),
      ...detail,
    });
    localStorage.setItem(STORAGE_LATIHAN, JSON.stringify(riwayat));
  } catch {}
  kirimSesiKeServer(modulId, skor, detail, dariTryout, tryoutId);
}

export function ambilRiwayatLatihan() {
  try {
    const data = localStorage.getItem(STORAGE_LATIHAN);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

/**
 * Ambil skor TERBAIK per modul (untuk kartu Latihan Harian)
 */
export function ambilSkorTerbaikPerModul() {
  const riwayat = ambilRiwayatLatihan();
  const hasil = {};
  riwayat.forEach(r => {
    if (!hasil[r.modulId] || r.skor > hasil[r.modulId]) {
      hasil[r.modulId] = r.skor;
    }
  });
  return hasil;
}

// ─── TRYOUT RESMI ─────────────────────────────────────────────

/**
 * Catat hasil tryout resmi (dipanggil dari sistem tryout nanti)
 * @param {number} nomorTryout - urutan tryout (1, 2, 3, ...)
 * @param {object} skorPerModul - { kraepelin: 85, logika: 72, ... }
 */
export function catatHasilTryout(nomorTryout, skorPerModul, detail = {}) {
  try {
    const riwayat = ambilRiwayatTryout();
    const skorAkumulasi = hitungSkorAkumulasiDariModul(skorPerModul);
    riwayat.push({
      id: `tryout-${Date.now()}`,
      nomorTryout,
      tanggal: new Date().toISOString(),
      skorPerModul,
      skorAkumulasi,
      ...detail,
    });
    localStorage.setItem(STORAGE_TRYOUT, JSON.stringify(riwayat));
  } catch {}
}

export function ambilRiwayatTryout() {
  try {
    const data = localStorage.getItem(STORAGE_TRYOUT);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

/**
 * Hitung skor akumulasi berbobot dari skor per modul (untuk tryout)
 */
export function hitungSkorAkumulasiDariModul(skorPerModul) {
  const modulDikerjakan = Object.keys(skorPerModul);
  if (modulDikerjakan.length === 0) return 0;
  let totalTerbobot = 0, totalBobot = 0;
  modulDikerjakan.forEach(modulId => {
    const bobot = BOBOT_MODUL[modulId] || 1;
    totalTerbobot += skorPerModul[modulId] * bobot;
    totalBobot += bobot;
  });
  return Math.round(totalTerbobot / totalBobot);
}

/**
 * Hitung skor akumulasi dari riwayat latihan harian
 * (dipakai untuk referensi saja, bukan untuk tryout)
 */
export function hitungSkorAkumulasi() {
  const skorTerbaik = ambilSkorTerbaikPerModul();
  const modulDikerjakan = Object.keys(skorTerbaik);
  if (modulDikerjakan.length === 0) {
    return { skor: null, modulDikerjakan: 0, totalModul: Object.keys(BOBOT_MODUL).length };
  }
  let totalTerbobot = 0, totalBobot = 0;
  modulDikerjakan.forEach(modulId => {
    const bobot = BOBOT_MODUL[modulId] || 1;
    totalTerbobot += skorTerbaik[modulId] * bobot;
    totalBobot += bobot;
  });
  return {
    skor: Math.round(totalTerbobot / totalBobot),
    modulDikerjakan: modulDikerjakan.length,
    totalModul: Object.keys(BOBOT_MODUL).length,
  };
}

export function resetRiwayat() {
  localStorage.removeItem(STORAGE_LATIHAN);
  localStorage.removeItem(STORAGE_TRYOUT);
}
